import React, { useEffect, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from 'emailjs-com';

const StyledTextField = styled(TextField)({
  backgroundColor: 'white',
  borderRadius: '4px',
  '& label.Mui-focused': {
    color: 'red',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'red',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'red',
    },
    '&:hover fieldset': {
      borderColor: 'red',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'red',
    },
  },
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});


export default function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [openEmailSent, setOpenEmailSent] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [openEmailError, setOpenEmailError] = useState(false);

  const recaptchaRef = useRef(null);

  const resetFormStateValues = () => {
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
    setRecaptchaToken(null);
  };

  const sendEmail = () => {
    setSendingEmail(true);
    const templateParams = {
      name: name,
      email: email,
      subject,
      message,
      'g-recaptcha-response': recaptchaToken,
    };

    emailjs.send('smtpout_plana', 'contact_plana', templateParams, 'user_ONPsdhMJoxCJENsXJ012q')
      .then((response) => {
        console.log(response);
        resetFormStateValues();
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
        setSendingEmail(false);
        setOpenEmailSent(true);
        console.log('Email sent');
      })
      .catch((err) => {
        console.error(err);
        setOpenEmailError(true);
        setSendingEmail(false);
      });
  };

  useEffect(() => {
    setDisableSubmit(name === ''
      || email === ''
      || subject === ''
      || message === ''
      || recaptchaToken === null);
  }, [
    name,
    email,
    subject,
    message,
    recaptchaToken,
  ]);

  useEffect(() => {
    if (openEmailSent) {
      setTimeout(() => {
        setOpenEmailSent(false);
      }, 12000);
    }
  }, [openEmailSent])

  return (
    <Box
      sx={{
        border: '2px solid white',
        borderRadius: '4px',
        padding: '24px',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography align="center" variant="h5" sx={{ fontWeight: 'bold' }}>
            Thank you for your interest in Plan <span style={{ color: 'red' }}>A </span>.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography align="center">
                Please submit inquiries using the form below.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography align="center">
                The first step for new clients is to book an intro session with me 
                { ' ' }
                <a
                  href='https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=5N3BQH9D4UNWU'
                  target='_blank'
                  rel="noreferrer"
                >
                  here
                </a>.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <StyledTextField
                  placeholder="Name *"
                  fullWidth
                  required
                  variant="outlined"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  placeholder="Email *"
                  fullWidth
                  required
                  variant="outlined"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  placeholder="Subject *"
                  fullWidth
                  required
                  variant="outlined"
                  value={subject}
                  onChange={(event) => {
                    setSubject(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  placeholder="Message *"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  value={message}
                  onChange={(event) => {
                    setMessage(event.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-end">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey='6LdxHNUcAAAAAMlupfO-pEoOD-o0brvmihxt_59K'
            onChange={(token) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
          />
        </Grid>
        <Grid item xs={12} container justifyContent="flex-end">
          <Tooltip
            sx={{
              fontSize: '14px',
            }}
            title={disableSubmit
            ? (
              <>
                {'You must fill in all required fields and complete the reCAPTCHA challenge before submitting.'}
                <br />
                {'Required fields are denoted by an asterisk.'}
              </>
            ) : ''}
            enterTouchDelay={100}
          >
            <span>
              <Button
                startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                sx={{
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  backgroundColor: 'red',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#9b0000',
                  },
                  '&:disabled': {
                    backgroundColor: 'lightGray',
                    color: 'gray',
                  },
                }}
                disabled={disableSubmit}
                onClick={sendEmail}
              >
                Submit
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Dialog
        open={openEmailSent}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="email-sent-description"
        PaperProps={{
          style: {
            backgroundColor: 'black',
            color: 'white',
            border: '2px solid white',
            borderRadius: '4px',
          },
        }}
        onClose={() => setOpenEmailSent(false)}
      >
        <DialogTitle>Thank you for your interest!</DialogTitle>
        <DialogContent>
          <DialogContentText id="email-sent-description" sx={{ color: 'white' }}>
            We look forward to possibly working with you!
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openEmailError}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="email-sent-description"
        PaperProps={{
          style: {
            backgroundColor: 'black',
            color: 'white',
            border: '2px solid white',
            borderRadius: '4px',
          },
        }}
        onClose={() => {
          setOpenEmailError(false);
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }}
      >
        <DialogTitle>Error Sending Email</DialogTitle>
        <DialogContent>
          <DialogContentText id="email-sent-description" sx={{ color: 'white' }}>
            There was an error sending your email. Please check your entries in the fields and try again.
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={sendingEmail}
      >
        <CircularProgress sx={{ color: 'red' }} />
      </Backdrop>
    </Box>    
  );
}
